/**
 * Takes a positive integer and returns the corresponding column name.
 * @param {number} num  The positive integer to convert to a column name.
 * @return {string}  The column name.
 * https://cwestblog.com/2013/09/05/javascript-snippet-convert-number-to-column-name/
 */
export const toSpreadsheetColumnName = (num: number): string => {
  let ret = '';
  for (let a = 1, b = 26; (num -= a) >= 0; a = b, b *= 26) {
    ret = String.fromCharCode(parseInt(`${(num % b) / a}`) + 65) + ret;
  }
  return ret;
};

/**
 * Takes in a cell address and returns an array containing the corresponding
 * column number followed by the row number.
 * @param {string} strCellAddr  The address of the cell (eg. AD32).
 * @return {!Array.<number>}  An array of two numbers:  the column number and
 *     the row number.
 * https://cwestblog.com/2013/09/04/javascript-snippet-parse-a-spreadsheet-address/
 */
export const parseSpreadsheetAddress = (strCellAddr: string): [number, number] => {
  let i = strCellAddr.search(/\d/);
  let colNum = 0;
  const cellAddr = +strCellAddr.replace(/\D/g, letter => {
    colNum += (parseInt(letter, 36) - 9) * 26 ** --i;
    return '';
  });
  return [colNum, cellAddr];
};

/**
 * Converts a spreadsheet column letter to a number. eg. A -> 1
 *
 * @param {String} str - (required) The spreadsheet column letter to convert to a number.
 *
 * @returns {(Number|undefined)} The column number or undefined.
 * https://stackoverflow.com/questions/22624379/how-to-convert-letters-to-numbers-with-javascript
 */
export const toSpreadsheetColumnNumber = (str: string | String): number | undefined => {
  if ((typeof str === 'string' || str instanceof String) && /^[a-zA-Z]+$/.test(str as string)) {
    str = str.toUpperCase();
    let out = 0,
      len = str.length;
    for (let pos = 0; pos < len; pos++) {
      out += (str.charCodeAt(pos) - 64) * 26 ** (len - pos - 1);
    }
    return out;
  } else {
    return undefined;
  }
};
